/*
Copyright 2026 WinSolutions

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type MatrixClient } from "matrix-js-sdk";

import { Config } from "./config/Config";
import { getHomeserverAssociatedDomain } from "./knownHomeserversMap";
import { ElementWidgetActions, widget, type WidgetHelpers } from "./widget";

export interface NativeCallIntentResponse {
  ok: boolean;
  sent: boolean;
  call_id: string;
  expires_at: number;
}

function validateIdentifiers(roomId: string, eventId: string): void {
  if (!roomId.startsWith("!") || !eventId.startsWith("$")) {
    throw new Error("Native call intent requires Matrix room and event IDs");
  }
}

/**
 * Dispatch a verified MatrixRTC ring to the native PushKit gateway.
 *
 * Embedded Element Call does not receive the host's Matrix access token. It
 * therefore passes only the non-secret room/event IDs to Element Web, which
 * authenticates the backend request. Standalone Element Call owns a regular
 * Matrix session and can authenticate the same request directly.
 */
export async function sendNativeCallIntent(
  client: MatrixClient,
  roomId: string,
  eventId: string,
  embeddedWidget: WidgetHelpers | null = widget,
): Promise<NativeCallIntentResponse | void> {
  validateIdentifiers(roomId, eventId);

  if (embeddedWidget) {
    await embeddedWidget.api.transport.send(
      ElementWidgetActions.NativeCallIntent,
      {
        roomId,
        rtcNotificationEventId: eventId,
      },
    );
    return;
  }

  const accessToken = client.getAccessToken();
  if (!accessToken) {
    throw new Error(
      "Standalone Element Call cannot dispatch a native call intent without a Matrix access token",
    );
  }

  const homeserver = client.getDomain() ?? Config.defaultServerName() ?? "";
  const backendDomain = getHomeserverAssociatedDomain(homeserver, "backend");
  const response = await fetch(`https://${backendDomain}/voip/call-intent`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      room_id: roomId,
      rtc_notification_event_id: eventId,
    }),
  });

  if (!response.ok) {
    throw new Error(
      `Native call intent request failed with status ${response.status}`,
    );
  }

  const result = (await response.json()) as NativeCallIntentResponse;
  if (!result.ok || result.call_id !== eventId) {
    throw new Error("Backend did not accept the native call intent");
  }

  return result;
}
